import moment from "moment-timezone";

const WALL_CLOCK_FORMAT = "YYYY-MM-DDTHH:mm:ss";

export const DATE_FORMAT = "MM/DD/YYYY";
export const DATE_TIME_FORMAT = "MM/DD/YYYY h:mmA";
export const SHORT_DATE_FORMAT = "MM/DD/YY";
export const DASHED_DATE_FORMAT = "MM-DD-YYYY";
export const DATE_TIME_WITH_ZONE_FORMAT = "MM/DD/YYYY h:mmA z";

// API date strings are intentionally excluded; parse them with parseWallClockDate first
type FormattableDate = Date | moment.Moment | number | null | undefined;

/**
 * Parses an API date string as a wall-clock time in the default timezone (see `moment.tz.setDefault`),
 * ignoring any trailing UTC offset. e.g. "2027-06-19T00:00:00Z" => midnight on 2027-06-19 in America/New_York.
 * @param {string} dateString - The API date string, e.g. "2027-06-19T00:00:00Z"
 * @returns {moment.Moment} A moment in the default timezone
 */
export const parseWallClockDate = (dateString: string) => moment(dateString, WALL_CLOCK_FORMAT);

/**
 * Converts an API wall-clock date string to a Date for date columns. Can be used directly as a DataGrid `valueGetter`.
 * @param {string | null | undefined} dateString - The API date string, e.g. "2027-06-19T00:00:00Z"
 * @returns {Date | null} A Date at that wall-clock time in the default timezone, or null when there is no date
 */
export const toWallClockDate = (dateString: string | null | undefined) => (dateString ? parseWallClockDate(dateString).toDate() : null);

/**
 * Parses an API timestamp that represents a real point in time (e.g. an "updated at" audit field), honoring its UTC offset,
 * and converts it to the default timezone. e.g. "2026-09-25T00:50:20.896150Z" => 09/24/2026 8:50PM in America/New_York.
 * @param {string} dateString - The API timestamp, e.g. "2026-09-25T00:50:20.896150Z"
 * @returns {moment.Moment} A moment in the default timezone
 */
export const parseTimestamp = (dateString: string) => moment(dateString);

/**
 * Formats a moment as a wall-clock string (no offset) that can be round-tripped through `parseWallClockDate`.
 * @param {moment.Moment} date - The moment to format
 * @returns {string} The formatted date string, e.g. "2027-06-19T00:00:00"
 */
export const formatWallClockDate = (date: moment.Moment) => date.format(WALL_CLOCK_FORMAT);

const formatInDefaultTimezone = (date: FormattableDate, format: string) => (date || date === 0 ? moment(date).format(format) : "");

/**
 * Formats a date in the default timezone. Can be used directly as a DataGrid `valueFormatter`.
 * @param {FormattableDate} date - The date to format
 * @returns {string} e.g. "06/19/2027", or "" when there is no date
 */
export const formatDate = (date: FormattableDate) => formatInDefaultTimezone(date, DATE_FORMAT);

/**
 * Formats a date and time in the default timezone. Can be used directly as a DataGrid `valueFormatter`.
 * @param {FormattableDate} date - The date to format
 * @returns {string} e.g. "06/19/2027 9:30AM", or "" when there is no date
 */
export const formatDateTime = (date: FormattableDate) => formatInDefaultTimezone(date, DATE_TIME_FORMAT);

/**
 * Formats a date with a two-digit year in the default timezone, for compact labels.
 * @param {FormattableDate} date - The date to format
 * @returns {string} e.g. "06/19/27", or "" when there is no date
 */
export const formatShortDate = (date: FormattableDate) => formatInDefaultTimezone(date, SHORT_DATE_FORMAT);

/**
 * Formats a date with dashes in the default timezone.
 * @param {FormattableDate} date - The date to format
 * @returns {string} e.g. "06-19-2027", or "" when there is no date
 */
export const formatDashedDate = (date: FormattableDate) => formatInDefaultTimezone(date, DASHED_DATE_FORMAT);

/**
 * Formats a date and time with the timezone abbreviation, in the default timezone.
 * @param {FormattableDate} date - The date to format
 * @returns {string} e.g. "09/24/2026 8:50PM EDT", or "" when there is no date
 */
export const formatDateTimeWithZone = (date: FormattableDate) => formatInDefaultTimezone(date, DATE_TIME_WITH_ZONE_FORMAT);
